import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { Button } from "@/components/design/Button";
import { MenuItem } from "@/components/design/MenuItem";
import { TextInput } from "@/components/design/TextField";
import { OrganizationalRecordBacklinksDocument, OrganizationalRecordLinkDocumentsDocument, OrganizationalRecordLinkEntriesDocument,
  OrganizationalRecordItemKind } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";

import type { OrganizationalRecordReference, OrganizationalRecordNavigate } from "./organizational-record-link-types";
export type { OrganizationalRecordReference, OrganizationalRecordNavigate } from "./organizational-record-link-types";
const keyOf = (link: OrganizationalRecordReference) => `${link.kind}:${link.id}`;

export function OrganizationalRecordLinkList({ links, onNavigate }: { links: readonly OrganizationalRecordReference[]; onNavigate?: OrganizationalRecordNavigate }) {
  const intl = useIntl();
  if (!links.length) return null;
  return <ul aria-label={intl.formatMessage({ id: "organizationalRecord.links.label" })} className="flex flex-wrap gap-2">
    {links.map(link => <li key={keyOf(link)} className="min-w-0 max-w-full"><Button variant="secondary" size="compact"
      className="max-w-full" onClick={() => onNavigate?.(link)} disabled={!onNavigate} title={link.title}>
      <span className="truncate">↗ {link.title}</span>
    </Button></li>)}
  </ul>;
}

/** A draft owned by the surrounding entry/comment form; selecting never writes on its own. */
export function OrganizationalRecordLinkEditor({ recordId, sourceEntryId, initial = [], value, disabled, onChange }: {
  recordId: string; sourceEntryId?: string; initial?: readonly OrganizationalRecordReference[]; value?: readonly OrganizationalRecordReference[]; disabled?: boolean;
  onChange: (links: OrganizationalRecordReference[], dirty: boolean) => void;
}) {
  const intl = useIntl();
  const [localSelected, setSelected] = useState<readonly OrganizationalRecordReference[]>(initial);
  const selected = value ?? localSelected;
  const [open, setOpen] = useState(false);
  const change = (next: OrganizationalRecordReference[]) => {
    setSelected(next);
    onChange(next, JSON.stringify(next.map(keyOf).sort()) !== JSON.stringify(initial.map(keyOf).sort()));
  };
  return <div className="space-y-2">
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="ghost" size="compact" disabled={disabled} aria-expanded={open} onClick={() => setOpen(value => !value)}>
        <FormattedMessage id="organizationalRecord.links.select" />
      </Button>
      {selected.length > 0 && <span className="field-meta tabular-nums text-ink/55">{selected.length}/20</span>}
    </div>
    {selected.length > 0 && <ul className="space-y-1">{selected.map(link => <li key={keyOf(link)} className="flex min-w-0 items-center gap-2">
      <span className="min-w-0 flex-1 truncate field-value" title={link.title}>{link.title}</span>
      <Button variant="ghost" size="sm" disabled={disabled} onClick={() => change(selected.filter(item => keyOf(item) !== keyOf(link)))}
        aria-label={intl.formatMessage({ id: "organizationalRecord.links.remove" }, { title: link.title })}>×</Button>
    </li>)}</ul>}
    {open && <LinkCandidates recordId={recordId} sourceEntryId={sourceEntryId} selected={selected} disabled={disabled}
      onSelect={link => change([...selected, link])} />}
  </div>;
}

function LinkCandidates({ recordId, sourceEntryId, selected, disabled, onSelect }: {
  recordId: string; sourceEntryId?: string; selected: readonly OrganizationalRecordReference[]; disabled?: boolean;
  onSelect: (link: OrganizationalRecordReference) => void;
}) {
  const intl = useIntl();
  const client = useApolloClient();
  const [state, setState] = useState<{ items: OrganizationalRecordReference[]; error: unknown; loading: boolean }>({ items: [], error: null, loading: true });
  const [retry, setRetry] = useState(0);
  const [search, setSearch] = useState("");
  useEffect(() => {
    let active = true;
    async function load() {
      const items: OrganizationalRecordReference[] = [];
      for (const kind of [OrganizationalRecordItemKind.Document, OrganizationalRecordItemKind.Entry]) {
        let after: string | undefined;
        const cursors = new Set<string>();
        while (true) {
          const input = { recordId, first: 100, ...(after ? { after } : {}) };
          const page = kind === OrganizationalRecordItemKind.Document
            ? (await client.query({ query: OrganizationalRecordLinkDocumentsDocument, variables: { input }, fetchPolicy: "no-cache" })).data?.organizationalRecordDocuments
            : (await client.query({ query: OrganizationalRecordLinkEntriesDocument, variables: { input }, fetchPolicy: "no-cache" })).data?.organizationalRecordEntries;
          if (!active) return;
          if (!page) throw new Error("Missing reference candidates");
          items.push(...page.nodes.map(item => ({ ...item, kind, entryId: null })));
          if (!page.pageInfo.hasNextPage) break;
          const cursor = page.pageInfo.endCursor;
          if (!cursor || cursors.has(cursor)) throw new Error("Reference cursor did not advance");
          cursors.add(cursor); after = cursor;
        }
      }
      if (active) setState({ items: Array.from(new Map(items.map(item => [keyOf(item), item])).values()), loading: false, error: null });
    }
    void load().catch(error => { if (active) setState({ items: [], loading: false, error }); });
    return () => { active = false; };
  }, [client, recordId, retry]);
  const candidates = state.items.filter(item => !(item.kind === OrganizationalRecordItemKind.Entry && item.id === sourceEntryId)
    && item.title.toLocaleLowerCase().includes(search.toLocaleLowerCase()));
  return <div className="box-flat space-y-2 bg-page p-3">
    <TextInput aria-label={intl.formatMessage({ id: "organizationalRecord.links.search" })} placeholder={intl.formatMessage({ id: "organizationalRecord.links.search" })}
      value={search} disabled={state.loading || disabled} onChange={event => setSearch(event.target.value)} />
    {state.loading && <p className="field-meta"><FormattedMessage id="common.loading" /></p>}
    {state.error ? <div role="alert" className="field-meta"><p>{userErrorMessage(intl, state.error)}</p>
      <Button variant="secondary" size="sm" onClick={() => { setState({ items: [], loading: true, error: null }); setRetry(value => value + 1); }}><FormattedMessage id="common.tryAgain" /></Button>
    </div> : null}
    <ul className="max-h-48 overflow-y-auto">{candidates.map(item => <li key={keyOf(item)}><MenuItem
      aria-label={intl.formatMessage({ id: "organizationalRecord.links.add" }, { title: item.title,
        kind: intl.formatMessage({ id: item.kind === OrganizationalRecordItemKind.Document ? "organizationalRecord.documents" : "organizationalRecord.entries" }) })}
      disabled={disabled || selected.length >= 20 || selected.some(value => keyOf(value) === keyOf(item))}
      onClick={() => onSelect(item)}>
      <span className="min-w-0"><span className="block truncate">{item.title}</span>
        <span className="field-meta text-ink/55"><FormattedMessage id={item.kind === OrganizationalRecordItemKind.Document ? "organizationalRecord.documents" : "organizationalRecord.entries"} /></span></span>
    </MenuItem></li>)}</ul>
    {!state.loading && !state.error && !candidates.length && <p className="field-meta text-ink/55"><FormattedMessage id="organizationalRecord.links.noMatches" /></p>}
  </div>;
}

export function OrganizationalRecordBacklinks({ recordId, target, onNavigate }: { recordId: string; target: OrganizationalRecordReference; onNavigate?: OrganizationalRecordNavigate }) {
  const intl = useIntl();
  const input = { recordId, target: { id: target.id, kind: target.kind }, first: 25 };
  const query = useQuery(OrganizationalRecordBacklinksDocument, { variables: { input }, fetchPolicy: "no-cache" });
  const [pageError, setPageError] = useState<unknown>(null);
  const page = query.data?.organizationalRecordBacklinks;
  return <div className="space-y-2 border-t border-ink/10 pt-4">
    <p className="field-label text-ink/55"><FormattedMessage id="organizationalRecord.links.usedIn" /></p>
    {(query.error || pageError) ? <p role="alert" className="field-meta">{userErrorMessage(intl, query.error ?? pageError)}</p> : null}
    {query.loading && <p className="field-meta"><FormattedMessage id="common.loading" /></p>}
    {!query.error && page?.nodes.map(link => <Button key={link.id} variant="secondary" size="sm" className="w-full justify-start"
      disabled={!onNavigate} onClick={() => onNavigate?.(link.source)} title={link.source.title}>
      <span className="min-w-0 truncate"><FormattedMessage id={link.source.kind === OrganizationalRecordItemKind.Comment ? "organizationalRecord.links.comment" : "organizationalRecord.entries"} />: {link.source.title}</span>
    </Button>)}
    {!query.loading && !query.error && !page?.nodes.length && <p className="field-meta text-ink/55"><FormattedMessage id="organizationalRecord.links.unused" /></p>}
    {page?.pageInfo.hasNextPage && <Button variant="secondary" size="sm" disabled={query.loading} onClick={() => {
      setPageError(null);
      void query.fetchMore({ variables: { input: { ...input, after: page.pageInfo.endCursor } },
        updateQuery: (previous, { fetchMoreResult: next }) => ({ ...next, organizationalRecordBacklinks: { ...next.organizationalRecordBacklinks,
          nodes: [...previous.organizationalRecordBacklinks.nodes, ...next.organizationalRecordBacklinks.nodes] } }),
      }).catch(setPageError);
    }}><FormattedMessage id="common.loadMore" /></Button>}
  </div>;
}
